<template>
  <template v-if="version === 'v1' && !sidebarComponents">
    <div
      class="property-list-item tw-mt-1 tw-flex tw-flex-col tw-px-5 tw-py-6 tw-text-start md:tw-min-h-[700px] md:tw-p-6"
      style="border-radius: var(--rounded)"
    >
      <nuxt-link
        :external="true"
        :to="property.detail_page_url"
        class="n-link tw-group tw-relative hover:tw-text-[unset]"
      >
        <div class="tw-relative">
          <LibraryImage
            :src="property.image"
            :h-ratio="10"
            :w-ratio="16"
            image-class="card-img tw-overflow-hidden"
            style="border-radius: var(--rounded)"
            :blur="imageBlurFilter"
            :pre-market-blur-text="preMarketBlurText"
          />
          <div
            v-if="property.custom_property_status_title"
            :class="[
              'tw-absolute tw-flex tw-items-center tw-justify-center tw-bg-black/50',
              property.is_locked ? 'tw-bottom-0 tw-left-0 tw-right-0' : 'tw-inset-0',
            ]"
          >
            <div class="tw-text-3xl tw-font-bold tw-text-white">
              {{ property.custom_property_status_title }}
            </div>
          </div>
        </div>

        <template v-if="!property.is_locked">
          <div class="tw-mt-6 tw-flex tw-flex-col">
            <small class="tw-mb-2 tw-uppercase">
              {{ property.price.type?.name || '\u00A0' }}
            </small>
            <h5 style="color: var(--c-secondary)">{{ property.price.price }}</h5>
          </div>
        </template>
        <template v-else-if="property.is_locked">
          <div class="tw-mt-6 tw-flex tw-flex-col">
            <h5>{{ preMarketBlurText || 'Pre-market Property' }}</h5>
          </div>
        </template>

        <div class="tw-mt-5">
          <h3
            class="n-title-primary tw-line-clamp-3 tw-max-h-[94px] tw-uppercase lg:tw-min-h-[94px]"
            style="letter-spacing: 4px !important"
          >
            {{ property.advert_heading }}
          </h3>
        </div>

        <div class="tw-mt-5 tw-flex tw-h-8 tw-flex-row tw-items-center tw-gap-2">
          <p><span style="color: var(--c-secondary)">A: </span>{{ property.display_address }}</p>
        </div>

        <template v-if="!property.is_locked">
          <div class="tw-mt-6 tw-flex tw-min-h-[32px] tw-flex-wrap tw-gap-x-6 tw-gap-y-3">
            <div v-if="property.bedroom">
              <p class="tw-flex tw-items-center tw-gap-2">
                <span style="color: var(--c-secondary)">BE:</span>{{ property.bedroom }} Beds
              </p>
            </div>
            <div v-if="property.bathroom">
              <p class="tw-flex tw-items-center tw-gap-2">
                <span style="color: var(--c-secondary)">BA:</span>{{ property.bathroom }} Baths
              </p>
            </div>
            <div v-if="property.reception">
              <p class="tw-flex tw-items-center tw-gap-2">
                <span style="color: var(--c-secondary)">RE:</span>{{ property.reception }} Reception
              </p>
            </div>
          </div>

          <button class="n-secondary outlined tw-mt-8">VIEW THIS PROPERTY</button>
        </template>

        <template v-else-if="property.is_locked">
          <p class="tw-py-2">
            This is a pre-market property. You need to create an account and register to our property alerts in order to
            view it.
          </p>
          <div class="tw-mt-10 tw-flex tw-gap-x-2">
            <nuxt-link :to="routes.REGISTER" class="n-primary tw-w-fit" :external="true">Register</nuxt-link>
            <nuxt-link
              :to="`${routes.PROPERTY_DETAIL.replace(':propertyId', property.property_profile_id)}#login`"
              class="n-primary tw-w-fit"
              :external="true"
              >Login</nuxt-link
            >
          </div>
        </template>
      </nuxt-link>
    </div>
  </template>

  <template v-if="version === 'v1' && sidebarComponents">
    <div
      class="property-list-item tw-mt-1 tw-flex tw-flex-col tw-px-5 tw-py-6 tw-text-start md:tw-p-6"
      style="border-radius: var(--rounded)"
    >
      <nuxt-link
        :external="true"
        :to="property.detail_page_url"
        class="n-link tw-group tw-relative hover:tw-text-[unset]"
      >
        <div class="tw-relative">
          <LibraryImage
            :src="property.image"
            :h-ratio="10"
            :w-ratio="16"
            image-class="card-img tw-overflow-hidden"
            style="border-radius: var(--rounded)"
            :blur="imageBlurFilter"
            :pre-market-blur-text="preMarketBlurText"
          />
          <div
            v-if="property.custom_property_status_title"
            :class="[
              'tw-absolute tw-flex tw-items-center tw-justify-center tw-bg-black/50',
              property.is_locked ? 'tw-bottom-0 tw-left-0 tw-right-0' : 'tw-inset-0',
            ]"
          >
            <div class="tw-text-3xl tw-font-bold tw-text-white">
              {{ property.custom_property_status_title }}
            </div>
          </div>
        </div>

        <template v-if="!property.is_locked">
          <div class="tw-mt-6 tw-flex tw-flex-col">
            <small class="tw-mb-2 tw-uppercase">
              {{ property.price.type?.name || '\u00A0' }}
            </small>
            <h5 style="color: var(--c-secondary)">{{ property.price.price }}</h5>
          </div>
        </template>
        <template v-else-if="property.is_locked">
          <div class="tw-mt-6 tw-flex tw-flex-col">
            <h5>{{ preMarketBlurText || 'Pre-market Property' }}</h5>
          </div>
        </template>

        <div class="tw-mt-5">
          <h3
            class="n-title-primary tw-line-clamp-3 tw-max-h-[90px] tw-uppercase lg:tw-min-h-[94px]"
            style="letter-spacing: 4px !important"
          >
            {{ property.advert_heading }}
          </h3>
        </div>

        <div class="tw-mt-5 tw-flex tw-h-8 tw-flex-row tw-items-center tw-gap-2">
          <p class="tw-capitalize"><span style="color: var(--c-secondary)">A: </span>{{ property.display_address }}</p>
        </div>

        <template v-if="!property.is_locked">
          <div class="tw-mt-6 tw-flex tw-min-h-[32px] tw-flex-wrap tw-gap-x-6 tw-gap-y-3">
            <div v-if="property.bedroom">
              <p class="tw-flex tw-items-center tw-gap-2">
                <span style="color: var(--c-secondary)">BE:</span>{{ property.bedroom }} Beds
              </p>
            </div>
            <div v-if="property.bathroom">
              <p class="tw-flex tw-items-center tw-gap-2">
                <span style="color: var(--c-secondary)">BA:</span>{{ property.bathroom }} Baths
              </p>
            </div>
            <div v-if="property.reception">
              <p class="tw-flex tw-items-center tw-gap-2">
                <span style="color: var(--c-secondary)">RE:</span>{{ property.reception }} Reception
              </p>
            </div>
          </div>

          <button class="n-secondary outlined tw-mt-8">VIEW THIS PROPERTY</button>
        </template>

        <template v-else-if="property.is_locked">
          <p class="tw-py-2">
            This is a pre-market property. You need to create an account and register to our property alerts in order to
            view it.
          </p>
          <div class="tw-mt-10 tw-flex tw-gap-x-2">
            <nuxt-link :to="routes.REGISTER" class="n-primary tw-w-fit" :external="true">Register</nuxt-link>
            <nuxt-link
              :to="`${routes.PROPERTY_DETAIL.replace(':propertyId', property.property_profile_id)}#login`"
              class="n-primary tw-w-fit"
              :external="true"
              >Login</nuxt-link
            >
          </div>
        </template>
      </nuxt-link>
    </div>
  </template>

  <template v-if="version === 'v2'">
    <div
      class="property-list-item tw-mt-1 tw-flex tw-flex-col tw-px-5 tw-py-6 tw-text-start md:tw-min-h-[700px] md:tw-p-6"
      style="border-radius: var(--rounded)"
    >
      <nuxt-link
        :external="true"
        :to="property.detail_page_url"
        class="n-link tw-group tw-relative hover:tw-text-[unset]"
      >
        <div class="custom-image-border-container tw-relative tw-m-2">
          <LibraryImage
            :src="property.image"
            :h-ratio="10"
            :w-ratio="16"
            image-class="card-img tw-overflow-hidden"
            style="border-radius: var(--rounded)"
            :blur="imageBlurFilter"
            :pre-market-blur-text="preMarketBlurText"
          />
          <div
            v-if="property.custom_property_status_title"
            :class="[
              'tw-absolute tw-flex tw-items-center tw-justify-center tw-bg-black/50',
              property.is_locked ? 'tw-bottom-0 tw-left-0 tw-right-0' : 'tw-inset-0',
            ]"
          >
            <div class="tw-text-3xl tw-font-bold tw-text-white">
              {{ property.custom_property_status_title }}
            </div>
          </div>
        </div>

        <div class="tw-mt-5 tw-flex tw-flex-wrap tw-items-center tw-justify-between">
          <h3
            class="n-title-primary tw-line-clamp-3 tw-max-h-[100px] tw-min-h-[100px] tw-uppercase lg:tw-max-h-[94px] lg:tw-min-h-[94px]"
            style="letter-spacing: 4px !important"
          >
            {{ property.advert_heading }}
          </h3>
          <div class="tw-flex tw-max-h-[94px] tw-min-h-[94px] tw-flex-col">
            <small class="tw-mb-2 tw-uppercase">
              {{ property.price.type?.name || '\u00A0' }}
            </small>
            <h5 style="color: var(--c-secondary)">{{ property.price.price }}</h5>
          </div>
        </div>

        <div class="tw-mt-5 tw-flex tw-h-8 tw-flex-row tw-items-center tw-gap-2">
          <p><span style="color: var(--c-secondary)">A: </span>{{ property.display_address }}</p>
        </div>
        <template v-if="!property.is_locked">
          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-mt-6 tw-flex tw-min-h-[32px] tw-flex-wrap tw-gap-x-6 tw-gap-y-3">
              <div v-if="property.bedroom">
                <p class="tw-flex tw-items-center tw-gap-2">
                  <span style="color: var(--c-secondary)">BE:</span>{{ property.bedroom }} Beds
                </p>
              </div>
              <div v-if="property.bathroom">
                <p class="tw-flex tw-items-center tw-gap-2">
                  <span style="color: var(--c-secondary)">BA:</span>{{ property.bathroom }} Baths
                </p>
              </div>
              <div v-if="property.reception">
                <p class="tw-flex tw-items-center tw-gap-2">
                  <span style="color: var(--c-secondary)">RE:</span>{{ property.reception }} Reception
                </p>
              </div>
            </div>
            <button
              :class="{
                'n-secondary outlined': !property.is_rent,
                'n-tertiary outlined': property.is_rent,
              }"
            >
              VIEW THIS PROPERTY
            </button>
          </div>
        </template>

        <template v-else-if="property.is_locked">
          <p class="tw-py-2">
            This is a pre-market property. You need to create an account and register to our property alerts in order to
            view it.
          </p>
          <div class="tw-mt-10 tw-flex tw-gap-x-2">
            <nuxt-link :to="routes.REGISTER" class="n-primary tw-w-fit" :external="true">Register</nuxt-link>
            <nuxt-link
              :to="`${routes.PROPERTY_DETAIL.replace(':propertyId', property.property_profile_id)}#login`"
              class="n-primary tw-w-fit"
              :external="true"
              >Login</nuxt-link
            >
          </div>
        </template>
      </nuxt-link>
    </div>
  </template>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import type { PropertyItemComponent } from '~/units/properties/types';
import NcTooltip from '~/components/common/NcTooltip.vue';
import { mapState } from 'pinia';
import { useWebsiteStore } from '~/units/website/store';
import { PropertyMixin } from '~/units/properties/mixins/property.mixin';

export default defineNuxtComponent({
  name: 'Theme8PropertyListItem',

  mixins: [PropertyMixin],

  components: { NcTooltip },

  props: {
    property: {
      required: true,
      type: Object as PropType<PropertyItemComponent>,
    },

    isMatchInformationHidden: {
      required: false,
      type: Boolean,
      default: false,
    },

    version: {
      required: false,
      type: String,
      default: 'v1',
    },

    preMarketPropertyFeature: {
      required: false,
      type: Boolean,
      default: false,
    },

    colSpan: {
      required: false,
      type: Number,
      default: 4,
    },

    isLetterPropertyCriteria: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(useWebsiteStore, ['sidebarComponents']),
  },
});
</script>

<style scoped>
.custom-image-border-container::before {
  content: '';
  position: absolute;
  top: -4px;
  right: -4px;
  width: 90%;
  height: 4px; /* Border thickness. Adjust if needed e.g. to 1px or 3px */
  background-color: var(--c-section_tertiary);
  z-index: 1; /* To ensure the border is rendered above the image */
}

.custom-image-border-container::after {
  content: '';
  position: absolute;
  top: 0;
  right: -4px;
  width: 4px; /* Border thickness. Adjust if needed e.g. to 1px or 3px */
  height: 90%;
  background-color: var(--c-section_tertiary);
  z-index: 1; /* To ensure the border is rendered above the image */
}
</style>
